import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getDealerProfile, saveDealerProfile, getOpportunityMeta, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const inputCls = "h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-atlas-blue/40 w-full";
const Field = ({ label, children, hint }) => (
  <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-500">{label}</span>{children}{hint && <span className="text-[11px] text-slate-400">{hint}</span>}</label>
);
const csv = (s) => (s ? s.split(",").map((x) => x.trim()).filter(Boolean) : []);

export default function DealerProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [p, setP] = useState({ company: "", country: "", destination_market: "", preferred_source_countries: [], preferred_brands: [], preferred_models: [], categories: [], age_min: "", age_max: "", mileage_min: "", mileage_max: "", budget_max: "", target_margin_pct: "", acceptable_risk: "Medium", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getOpportunityMeta().then(setMeta).catch(() => {});
    getDealerProfile().then((d) => { if (d.profile) setP((prev) => ({ ...prev, ...d.profile, target_margin_pct: d.profile.target_margin_pct != null ? d.profile.target_margin_pct * 100 : "" })); }).catch(() => {});
  }, []);

  if (user === null) return <><div className="p-10 text-center text-slate-400">Checking access…</div></>;
  if (!user) { navigate("/login"); return null; }

  const save = async () => {
    setBusy(true);
    try {
      await saveDealerProfile({
        ...p,
        preferred_source_countries: p.preferred_source_countries, preferred_brands: p.preferred_brands,
        age_min: p.age_min ? Number(p.age_min) : null, age_max: p.age_max ? Number(p.age_max) : null,
        mileage_min: p.mileage_min ? Number(p.mileage_min) : null, mileage_max: p.mileage_max ? Number(p.mileage_max) : null,
        budget_max: p.budget_max ? Number(p.budget_max) : null,
        target_margin_pct: p.target_margin_pct ? Number(p.target_margin_pct) / 100 : null,
      });
      toast.success("Dealer profile saved.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-8">
        <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Atlas Intelligence · profile</span>
        <h1 className="text-3xl font-bold text-atlas-ink mt-1 mb-1">Dealer profile</h1>
        <p className="text-slate-500 text-sm mb-6">Your sourcing preferences shape which opportunities Atlas prioritises for you — the goal is “the best vehicle for you”, not “the best vehicle in Europe”.</p>

        <div className="rounded-lg border border-slate-200 bg-white p-6 space-y-5" data-testid="dealer-profile-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company"><input className={inputCls} value={p.company || ""} onChange={(e) => setP({ ...p, company: e.target.value })} data-testid="dp-company" /></Field>
            <Field label="Base country">
              <select className={inputCls} value={p.country || ""} onChange={(e) => setP({ ...p, country: e.target.value })} data-testid="dp-country">
                <option value="">—</option>
                {(meta?.countries || []).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Primary destination market">
              <select className={inputCls} value={p.destination_market || ""} onChange={(e) => setP({ ...p, destination_market: e.target.value })} data-testid="dp-destination">
                <option value="">—</option>
                {(meta?.countries || []).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Acceptable risk">
              <select className={inputCls} value={p.acceptable_risk || "Medium"} onChange={(e) => setP({ ...p, acceptable_risk: e.target.value })} data-testid="dp-risk">
                <option>Low</option><option>Medium</option><option>High</option>
              </select>
            </Field>
            <Field label="Preferred brands" hint="comma-separated"><input className={inputCls} value={(p.preferred_brands || []).join(", ")} onChange={(e) => setP({ ...p, preferred_brands: csv(e.target.value) })} data-testid="dp-brands" /></Field>
            <Field label="Preferred models" hint="comma-separated"><input className={inputCls} value={(p.preferred_models || []).join(", ")} onChange={(e) => setP({ ...p, preferred_models: csv(e.target.value) })} /></Field>
            <Field label="Budget max"><input className={inputCls} type="number" value={p.budget_max || ""} onChange={(e) => setP({ ...p, budget_max: e.target.value })} data-testid="dp-budget" /></Field>
            <Field label="Target margin %"><input className={inputCls} type="number" value={p.target_margin_pct || ""} onChange={(e) => setP({ ...p, target_margin_pct: e.target.value })} data-testid="dp-margin" /></Field>
            <Field label="Min year"><input className={inputCls} type="number" value={p.age_min || ""} onChange={(e) => setP({ ...p, age_min: e.target.value })} /></Field>
            <Field label="Max mileage (km)"><input className={inputCls} type="number" value={p.mileage_max || ""} onChange={(e) => setP({ ...p, mileage_max: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><textarea className="rounded-md border border-slate-300 px-3 py-2 text-sm" rows={2} value={p.notes || ""} onChange={(e) => setP({ ...p, notes: e.target.value })} /></Field>
          <Button disabled={busy} onClick={save} className="bg-atlas-blue hover:bg-atlas-sky text-white h-11" data-testid="dp-save">{busy ? "Saving…" : "Save profile"}</Button>
        </div>
      </div>
    </>
  );
}
